import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Line } from 'react-chartjs-2';
import '@/lib/chartjs-register';
import { convertChartOptions, ChartJS } from '@/lib/chartjs-register';
import ChartWidget from '@/components/ui/shared/ChartWidget';
import ColorJson from '../whitelabel.json';

/**
 * LineListChart - Functional component replacement for LineList.js
 * Renders a line chart with list view toggle
 */
function LineListChart({ data, startDate, endDate, _Index }) {
    const [chartData, setChartData] = useState([]);
    const [listData, setListData] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [legendText, setLegendText] = useState('');
    const [legendValue, setLegendValue] = useState('');
    const [activePage, setActivePage] = useState(1);
    const [noDataMessage, setNoDataMessage] = useState('');
    const abortControllerRef = useRef(null);

    // Set global Chart.js font - matching other widgets (IBM Plex Sans)
    useEffect(() => {
        if (typeof window !== 'undefined' && window.Chart) {
            window.Chart.defaults.global.defaultFontFamily = `'IBM Plex Sans', sans-serif`;
        }
        // For Chart.js v4
        if (ChartJS?.defaults?.font) {
            ChartJS.defaults.font.family = "'IBM Plex Sans', sans-serif";
        }
    }, []);

    const isRxTheme = useMemo(() => {
        const domain = window.location.host.split('.').slice(-2).join('.').toLowerCase();
        return domain === "rxhacker.com" || 
               domain === "localhost:5464" || 
               window.location.host.toLowerCase() === "tempfilter.23byt.es" || 
               window.location.host.toLowerCase() === "reporting.nulifespanrx.com";
    }, []);

    const themeColors = useMemo(() => {
        if (isRxTheme) {
            return {
                borderColor: ColorJson.ApplicationColor.Rxhacker_secondary_color,
                hoverBg: ColorJson.ApplicationColor.Rxhacker_extra_color
            };
        }
        return {
            borderColor: '#2A524B',
            hoverBg: '#FF8B6C'
        };
    }, [isRxTheme]);

    const fetchData = useCallback(async () => {
        if (!data?.url || !data?.RequestJson) return;

        // Cancel any previous request
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }
        abortControllerRef.current = new AbortController();

        setIsLoading(true);
        setNoDataMessage('Loading. Please wait.');

        try {
            const response = await fetch(data.url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'requestHeader': data.RequestJson1 || '',
                    'X-Browser-Url': window.location.href
                },
                body: data.RequestJson,
                signal: abortControllerRef.current.signal
            });

            // Handle response like the original class component
            const text = await response.text();
            const apidata = text ? JSON.parse(text) : null;

            if (apidata?.[0]) {
                // Set legend
                if (apidata[0].legend?.[0]) {
                    setLegendText(apidata[0].legend[0].legendText);
                    setLegendValue(apidata[0].legend[0].legendValue);
                }

                // Check for data availability - same logic as original
                const hasData = apidata[0].lineDataSet?.[0]?.datasets?.length > 0;
                const dataAvailableMsg = hasData ? '' : 'No Data Available';
                setNoDataMessage(dataAvailableMsg);

                // Set chart data
                if (apidata[0].lineDataSet?.[0]) {
                    const lineDataset = apidata[0].lineDataSet[0];
                    
                    setChartData([{
                        datasets: [{
                            label: lineDataset.toplabel,
                            data: lineDataset.datasets,
                            backgroundColor: '#fff',
                            borderColor: themeColors.borderColor,
                            pointBackgroundColor: '#fff',
                            pointBorderColor: themeColors.borderColor,
                            pointBorderWidth: 2,
                            pointRadius: 4,
                            pointHoverRadius: 6,
                            fill: false,
                            hoverBackgroundColor: themeColors.hoverBg,
                            tension: 0,
                            borderWidth: 2
                        }]
                    }]);
                }

                // Set list data
                if (apidata[0].listDataSet) {
                    setListData(apidata[0].listDataSet);
                }

                setActivePage(1);
                setIsLoading(false);  // Set to false, not true!
            } else {
                setChartData([]);
                setListData([]);
            }
        } catch (err) {
            if (err.name !== 'AbortError') {

                setNoDataMessage('Error while loading data');
            }
        } finally {
            setIsLoading(false);
        }
    }, [data?.url, data?.RequestJson, data?.RequestJson1, themeColors]);

    useEffect(() => {
        if (data?.type === 'Line_List' && data?.RequestJson && data?.url) {
            fetchData();
        }
        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, [data?.RequestJson, data?.type, fetchData]);

    const chartOptions = useMemo(() => {
        // Use Chart.js v4 format directly (like BarListChart)
        return {
            maintainAspectRatio: false,
            responsive: true,
            plugins: {
                legend: {
                    display: false,
                },
                tooltip: {
                    enabled: true,
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    titleColor: '#1d252d',
                    bodyColor: '#1d252d',
                    borderColor: '#e2e8f0',
                    borderWidth: 1,
                    padding: 12,
                    cornerRadius: 8,
                    titleFont: {
                        size: 12,
                        weight: 'normal',
                    },
                    bodyFont: {
                        size: 14,
                        weight: 'bold',
                    },
                },
            },
            scales: {
                y: {
                    beginAtZero: true,
                    border: {
                        display: false,
                    },
                    grid: {
                        display: true,
                        drawBorder: false,
                    },
                    ticks: {
                        color: '#94a3b8',
                        font: {
                            size: 12,
                        },
                    },
                    title: {
                        display: !!data?.YlabelString,
                        text: data?.YlabelString || '',
                        color: '#94a3b8',
                        font: {
                            size: 12,
                        },
                    },
                },
                x: {
                    type: 'time',
                    time: {
                        unit: 'day',
                        parser: 'MM/dd/yyyy',
                        tooltipFormat: 'MMM d',
                        displayFormats: {
                            day: 'MMM d'
                        }
                    },
                    border: {
                        display: false,
                    },
                    grid: {
                        display: false,
                        drawBorder: false,
                    },
                    ticks: {
                        display: false,
                        color: '#94a3b8',
                        font: {
                            size: 11,
                        },
                    },
                    title: {
                        display: false,
                        text: data?.XlabelString || '',
                        color: '#94a3b8',
                        font: {
                            size: 12,
                        },
                    },
                },
            },
            interaction: {
                intersect: false,
                mode: 'index',
            },
            onHover: (event, elements) => {
                const canvas = event.native?.target;
                if (canvas) {
                    canvas.style.cursor = elements.length > 0 ? 'pointer' : 'default';
                }
            },
        };
    }, [data?.YlabelString, data?.XlabelString]);

    const handleExport = useCallback(() => {
        try {
            const rows = listData.map(row => {
                return row.datasets.map((cell, cellIndex) => {
                    // Explicitly convert to string and remove commas
                    let cellValue = String(cell.data || '').replace(/,/g, '');
                    
                    // Check if this looks like a date in format XX/XX or XX/XX/XXXX in the first column
                    // Add a leading space to prevent Excel from auto-formatting dates
                    if (cellIndex === 0 && /^\d{1,2}\/\d{1,2}(\/\d{2,4})?$/.test(cellValue)) {
                        cellValue = ` ${cellValue}`;
                    }
                    
                    return cellValue;
                }).join(',');
            });
            const csvContent = rows.join('\n');
            const blob = new Blob([csvContent], { type: 'text/csv' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.download = `${data?.title || 'chart'}.csv`;
            link.href = url;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } catch (error) {

        }
    }, [listData, data?.title]);

    const chartContent = useMemo(() => {
        if (!chartData.length || !chartData[0]?.datasets?.[0]?.data?.length) {
            return null;
        }
        return (
            <div className="h-full w-full">
                <Line data={chartData[0]} options={chartOptions} />
            </div>
        );
    }, [chartData, chartOptions]);

    const listHeaders = useMemo(() => {
        if (listData.length > 0 && listData[0]?.datasets) {
            return listData[0].datasets.map(d => d.data);
        }
        return [];
    }, [listData]);

    const listDataForWidget = useMemo(() => {
        if (listData.length <= 1) return [];
        // Skip the header row and convert datasets to simple arrays
        return listData.slice(1).map(row => 
            row.datasets ? row.datasets.map(d => d.data) : []
        );
    }, [listData]);

    return (
        <ChartWidget
            title={data?.title}
            subtitle={data?.Subtitle}
            legendValue={legendValue}
            isLoading={isLoading}
            onExport={handleExport}
            noDataMessage={noDataMessage}
            hasData={chartData.length > 0 && chartData[0]?.datasets?.[0]?.data?.length > 0}
            chartContent={chartContent}
            listHeaders={listHeaders}
            listData={listDataForWidget}
            activePage={activePage}
            onPageChange={setActivePage}
            itemsPerPage={6}
            totalItems={listData.length > 1 ? listData.length - 1 : 0}
            chartType="Line"
        />
    );
}

export default LineListChart;

